import { createSlice } from '@reduxjs/toolkit';
import { GridStackWidget } from 'gridstack';
import { isNotNil } from 'ramda';
import { Graph } from '../Components/Dashboard/dashboardTypes';
import { ApplicationActions, RobotActions } from '../utils/constants';
import { isSyntheticDriveUnitPortElectronic } from '../utils/isSyntheticDriveUnitPortElectronic';
import type { PendingLinterIgnore } from '../utils/linterIgnoreIntent';
import { projectSlice } from './ProjectSlice';
import { robotSlice } from './RobotSlice';

const { Dialogs } = ApplicationActions;

export interface DialogOptions {
  title?: string;
  projectId?: string;
  projectName?: string;
  pathId?: string;
  isEditing?: boolean;
  robotTypeName?: string;
  fullWidth?: boolean;
  maxWidth?: string;
  disableEscapeKeyDown?: boolean;
  selectedRobot?: string;
  projectTemplate?: string | null;
  rapidRobotCode?: string | null;
  saveFromSwerveSlot?: boolean;
  swerveSlotAssetId?: string;
  swerveSlotProgramTitle?: string;
  swerveSlotBpsProjectId?: string;
  swerveSlotBpsProjectLastModifiedUtcMs?: number;
  /** Local project id to duplicate when naming a copy from read-only viewer mode. */
  copyFromProjectId?: string;
  newShareCode?: boolean;
  /** Port number (1-8) for electronic check dialogs */
  port?: number;
  /** Electronic type identifier for dialogs that render device-specific controls. */
  electronicType?: string;
  /** Pyright/LSP diagnostics shown in the linter errors dialog. */
  diagnostics?: Array<{ message: string; severity?: number }>;
  /** Primary action label for the linter errors dialog ignore button. */
  linterIgnoreAction?: 'run' | 'deploy';
}

const emptyDialog = {
  dialog: null,
  options: {
    title: '',
    projectId: '',
    isEditing: false,
    fullWidth: false,
    maxWidth: 'sm',
    disableEscapeKeyDown: true,
    selectedRobot: '',
    project: null,
  },
};

/** One row in the drive-unit port occupancy map (multiple rows may share the same `driveUnitPort`). */
export interface DriveUnitPortOccupancy {
  driveUnitPort: number;
  electronic: string;
}

interface UserAgentInfo {
  browser: string | number | undefined;
  version: string | number | undefined;
  os: string;
  hasBluetooth: boolean;
  isBrowserSupported: boolean;
}

interface ApplicationSlice {
  exitProgram: boolean;
  warnings: Array<unknown> | null;
  enableToolBar: boolean;
  onButton: null;
  hoveredControllerButton: string | null;
  layerView: boolean;
  openDialog: { dialog: string | null; options: DialogOptions };
  nextDialog: { dialog: string | null; options: DialogOptions } | null;
  saveProgram: boolean;
  userAgentInfo: UserAgentInfo | null;
  cookiesBannerOpen: boolean;
  hideQuotaDialog: boolean;
  saveLayout: Array<GridStackWidget>;
  driveUnitPorts: DriveUnitPortOccupancy[];
  graphs: Array<Graph>;
  runDate: number | null;
  dashboardViewMode: (typeof ApplicationActions.DashboardViewMode)[keyof typeof ApplicationActions.DashboardViewMode];
  controllerConnected: boolean;
  autoSwapToDashboard: boolean;
  pendingLinterIgnore: PendingLinterIgnore | null;
}

const initialState: ApplicationSlice = {
  exitProgram: false,
  warnings: null,
  enableToolBar: true,
  onButton: null,
  hoveredControllerButton: null,
  layerView: false,
  openDialog: emptyDialog,
  nextDialog: null,
  saveProgram: false,
  userAgentInfo: <UserAgentInfo | null>null,
  cookiesBannerOpen: true,
  hideQuotaDialog: false,
  saveLayout: [],
  driveUnitPorts: [] as DriveUnitPortOccupancy[],
  graphs: [],
  runDate: <number | null>null,
  dashboardViewMode: ApplicationActions.DashboardViewMode.LIVE,
  controllerConnected: false,
  autoSwapToDashboard: true,
  pendingLinterIgnore: null,
};

export const applicationSlice = createSlice({
  name: 'application',
  initialState,
  reducers: {
    exitProgram: (state, { payload }) => ({
      ...state,
      exitProgram: payload,
    }),
    onUpdateToolbarState: (state, { payload }) => ({ ...state, enableToolBar: payload }),
    onToggleLayerView: (state) => ({ ...state, layerView: !state.layerView }),
    setLayerView: (state, { payload }: { payload: boolean }) => ({ ...state, layerView: payload }),
    onOpenDialog: (state, { payload }) => {
      // we no longer have a current dialog, but we prevously set a next dialog to open
      if (!isNotNil(payload.dialog) && isNotNil(state.nextDialog)) {
        return { ...state, openDialog: state.nextDialog, nextDialog: null };
      }
      return { ...state, openDialog: payload };
    },
    onHoverControllerButton: (state, { payload }) => ({ ...state, hoveredControllerButton: payload }),
    onSetNextDialog: (state, { payload }) => ({ ...state, nextDialog: payload }),
    onSaveProgram: (state, { payload }) => ({ ...state, saveProgram: payload }),
    onUpdateUserAgentInfo: (state, { payload }) => ({ ...state, userAgentInfo: payload }),
    onUpdateCookiesBannerOpen: (state, { payload }) => ({ ...state, cookiesBannerOpen: payload }),
    onSetHideQuotaDialog: (state, { payload }) => ({ ...state, hideQuotaDialog: payload }),
    onSaveLayout: (state, { payload }) => ({ ...state, saveLayout: payload }),
    onUpdateDriveUnitPorts: (state, { payload }) => {
      const { driveUnitPort, electronic } = payload;

      const existingElectronicIndex = state.driveUnitPorts.findIndex((port) => port.electronic === electronic);
      if (existingElectronicIndex !== -1) {
        state.driveUnitPorts[existingElectronicIndex].driveUnitPort = driveUnitPort;
        return;
      }

      // First registration for this electronic: append whether or not another device already uses this port
      // (multiple `electronic` rows may reference the same `driveUnitPort` until the user resolves overlaps).
      state.driveUnitPorts.push({ driveUnitPort, electronic });
    },
    /** Drops one occupancy row when code no longer has a valid DU port for that electronic (e.g. NaN or out of range). */
    onRemoveDriveUnitPortOccupancy: (state, { payload }) => {
      const { electronic } = payload;
      state.driveUnitPorts = state.driveUnitPorts.filter((row) => row.electronic !== electronic);
    },
    /** Clears port-occupancy map when hardware cards are rebuilt (e.g. device removed from code). */
    resetDriveUnitPorts: (state) => {
      state.driveUnitPorts = [];
    },
    /** Removes hardware-derived rows while keeping transient dialog reservations (snippet / action-arg drops). */
    clearNonSyntheticDriveUnitPorts: (state) => {
      state.driveUnitPorts = state.driveUnitPorts.filter((row) => isSyntheticDriveUnitPortElectronic(row.electronic));
    },
    onSaveGraphs: (state, { payload }) => ({ ...state, graphs: payload }),
    onSetRunDate: (state, { payload }) => ({ ...state, runDate: payload }),
    onSetDashboardViewMode: (state, { payload }) => ({ ...state, dashboardViewMode: payload }),
    onSetControllerConnected: (state, { payload }) => ({ ...state, controllerConnected: payload }),
    onSetAutoSwapToDashboard: (state, { payload }) => ({ ...state, autoSwapToDashboard: payload }),
    setPendingLinterIgnore: (state, { payload }) => ({
      ...state,
      pendingLinterIgnore: { intent: payload, confirmed: false },
    }),
    confirmPendingLinterIgnore: (state) => {
      if (state.pendingLinterIgnore) {
        state.pendingLinterIgnore.confirmed = true;
      }
    },
    clearPendingLinterIgnore: (state) => ({
      ...state,
      pendingLinterIgnore: null,
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(projectSlice.actions.onLoadProject, (state) => ({
      ...state,
      dashboardViewMode: ApplicationActions.DashboardViewMode.LIVE,
      saveLayout: [],
      pendingLinterIgnore: null,
    }));
    builder.addCase(projectSlice.actions.onCreateProject, (state) => ({
      ...state,
      pendingLinterIgnore: null,
    }));
    builder.addCase(projectSlice.actions.onUpdateProgramState, (state, { payload }) => {
      if (payload.programState === RobotActions.PROGRAM.STOP) return { ...state, softwareButtons: [], onButton: null, liveSensorData: null };
      if (payload.programState === RobotActions.PROGRAM.STARTING && state.dashboardViewMode === ApplicationActions.DashboardViewMode.HISTORIC) {
        return { ...state, dashboardViewMode: ApplicationActions.DashboardViewMode.LIVE };
      }
    });
    builder.addCase(robotSlice.actions.onUpdateRobotState, (state, { payload }) => {
      if (payload === RobotActions.STATE.CONNECT_FAILED || payload === RobotActions.STATE.CONNECT_FAILED_IN_CHARGER) {
        return { ...state, openDialog: Dialogs.CONNECTION_PROGRESS };
      }
    });
    builder.addCase(robotSlice.actions.onUpdateDiscovery, (state, { payload }) => {
      if (payload.discoveryState === RobotActions.DISCOVERY.DISCOVERED) {
        return { ...state, openDialog: Dialogs.CONNECTION_PROGRESS };
      }
    });
  },
});

export default applicationSlice.reducer;
